import React from "react";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import { OSSTooltip, OSSMultiSelectAutocomplete } from "@oss/components";

const useStylesFilter = makeStyles(theme => ({
  capitalize: {
    textTransform: "capitalize"
  },
  selectionCount: {
    background: "#666",
    color: "white",
    padding: [[theme.spacing(0.5), theme.spacing(1)]],
    borderRadius: 4
  },
  selectionList: { listStyle: "none", padding: 0, margin: 0 },
  endAdornment: { position: "absolute", right: 9, top: 8 },
  optionSelected: {
    fontWeight: "bold"
  },
  option: { width: "100%" }
}));

/**
 * @param filter configs have the following settings:
 * @param displayFunction: funtion for the display of the current option
 * @param name: the url get param key
 * @param filterKey: object key for the value to send in query, ie ID
 * @param matchKey: object key for the value to display in the front end options
 * @param options: an array of options to display
 * @param onSelect: function to execute when a option selection is made
 */
const OssMultiSelectAutoSelWithToolTip = ({
  filter,
  query,
  fullWidth = true,
  showSelectedValues = true,
  showSelectionCount = true,
  InputProps = {},
  inputProps = { style: { padding: 1 } },
  selectedvalues,
  ...rest
}) => {
  const classes = useStylesFilter();
  const {
    name,
    matchKey = "val",
    placeholder,
    filterKey = "val",
    onSelect,
    displayFunction,
    multiple = true,
    defaultValues = [],
  } = filter;
  let { options = [] } = filter;

  //for consistency, want to transform our options into an array of objects
  //even if provided an array of strings
  options = options.map(option =>
    typeof option === "string" ? { val: option } : option
  );

  //get the current values for this filter from the query,
  //which can be an array, string, or empty
  const selectedValues = Array.isArray(query[name])
    ? query[name]
    : !query[name]
    ? []
    : [query[name]];

  //the list of options which should be selected
  const selectedOptions = options.filter(option =>
    selectedValues.includes(option[filterKey])
  );

  const handleUpdate = (evt, values) => {
    let selectedValues = [];
    switch (true) {
      case Array.isArray(values):
        selectedValues = values.map(value => value[filterKey]);
        break;
      case values === null:
        selectedValues = defaultValues || [];
        break;
      case typeof values === "object":
        selectedValues = values[filterKey];
        break;
      default:
        break;
    }
    onSelect(name, selectedValues);
  };

  const getSelectedTooltip = endAdornment => {
    return (
      <div className={classes.endAdornment}>
        {showSelectionCount && selectedValues.length ? (
          <OSSTooltip
            title={
              <ul className={classes.selectionList}>
                {selectedOptions.map((option, index) => (
                  <li key={index}>{option[matchKey]}</li>
                ))}
              </ul>
            }
            interactive
            leaveDelay={300}
          >
            <span className={classes.selectionCount}>
              {selectedValues.length}
            </span>
          </OSSTooltip>
        ) : null}
        {endAdornment.props.children}
      </div>
    );
  };

  const getPlaceholder = () => {
    if (showSelectedValues) {
      return selectedValues.length
        ? selectedOptions.map(option => option[matchKey]).join(", ")
        : placeholder;
    } else {
      return placeholder;
    }
  };

  return (
    <OSSMultiSelectAutocomplete
      matchKey={matchKey}
      classes={{
        option: classes.option
      }}
      multiple={multiple}
      size="small"
      options={options}
      value={selectedOptions}
      getOptionDisabled={option => option.disabled}
      disableListWrap
      renderTags={() => null}
      renderInput={params => {
        return (
          <TextField
            {...params}
            placeholder={selectedvalues ? selectedvalues : getPlaceholder()}
            size="small"
            variant="outlined"
            multiline={false}
            className={classes.capitalize}
            fullWidth={fullWidth}
            InputProps={{
              ...params.InputProps,
              endAdornment: getSelectedTooltip(params.InputProps.endAdornment),
              ...InputProps
            }}
            inputProps={{ ...params.inputProps, ...inputProps }}
          />
        );
      }}
      renderOption={(props, option, state) => {
        const { className, ...rest } = props;
        return (
          <div
            data-testid="select-option"
            {...rest}
            className={[
              classes.option,
              ...className.split(" "),
              state.selected && classes.optionSelected
            ].join(" ")}
          >
            {displayFunction ? displayFunction(option) : option[matchKey]}
          </div>
        );
      }}
      onChange={handleUpdate}
      {...rest}
    />
  );
};

export default OssMultiSelectAutoSelWithToolTip;
